import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Dialog } from "@arcadeai/design-system/components/ui/atoms/dialog";
import { DropdownMenuSeparator } from "@arcadeai/design-system/components/ui/atoms/dropdown-menu";
import {
  OrganizationSwitcher as BaseOrganizationSwitcher,
  type OrganizationSwitcherProps as BaseOrgSwitcherProps,
  type SwitcherEntity,
} from "@arcadeai/ui-kit/org-project-context/organization-switcher";
import { SwitcherItem } from "@arcadeai/ui-kit/org-project-context/switcher";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Pencil, Settings } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { hasOrgPermission } from "@/app/capabilities/api/check-permission";
import { orgCapabilitiesQueryOptions } from "@/app/capabilities/api/get-capabilities";
import { ORG } from "@/app/capabilities/permissions";
import { useGetOrganizations } from "@/app/organizations/api/hooks";
import type { Organization } from "@/app/organizations/types";
import { ORG_PROJECT_ROUTE_PATH } from "@/lib/org-project-context/constants";
import {
  resolveDashboardOrgProjectRoute,
  useDashboardOrgProject,
  useNavigateToScope,
  useOrgProjectRouteContext,
} from "@/lib/org-project-context/hooks";
import { EditOrganizationDialog } from "./edit-organization-dialog";

export type OrganizationSwitcherProps = Pick<
  BaseOrgSwitcherProps,
  "triggerClassName" | "triggerVariant" | "triggerSize"
>;

export function OrganizationSwitcher({
  triggerClassName,
  triggerVariant = "outline",
  triggerSize = "sm",
}: OrganizationSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingOrganization, setEditingOrganization] =
    useState<Organization | null>(null);

  const { activeOrganizationId, activeProjectId } = useDashboardOrgProject();
  const { organizationId } = useOrgProjectRouteContext();
  const { data: organizations } = useGetOrganizations();
  const navigateToScope = useNavigateToScope();
  const queryClient = useQueryClient();

  // The org page has independently gated metadata and members sections, so only
  // offer the "Manage organization" action when either section is reachable.
  const { authz } = useSuspenseQuery(
    orgCapabilitiesQueryOptions(organizationId)
  ).data;
  const canManageOrg =
    authz.enabled &&
    (hasOrgPermission(authz, ORG.orgMetadata.read) ||
      hasOrgPermission(authz, ORG.members.read));

  const orgLookup = useMemo(
    () => new Map(organizations.map((o) => [o.organization_id, o])),
    [organizations]
  );

  const entities: SwitcherEntity[] = useMemo(
    () =>
      organizations.map((o) => ({
        id: o.organization_id,
        name: o.name,
        description: o.description,
      })),
    [organizations]
  );

  const handleSelect = useCallback(
    async (targetOrganizationId: string) => {
      if (targetOrganizationId === activeOrganizationId) {
        setIsOpen(false);
        return;
      }

      try {
        const result = await resolveDashboardOrgProjectRoute(queryClient, {
          organizationId: targetOrganizationId,
          projectId: activeProjectId ?? undefined,
        });

        if (result.outcome === "valid" || result.outcome === "redirect") {
          navigateToScope({
            organizationId: result.organizationId,
            projectId: result.projectId,
          });
          return;
        }

        if (result.fallbackContext) {
          navigateToScope(result.fallbackContext);
        }
      } catch {
        toast.error("Failed to switch organization. Please try again.");
      } finally {
        setIsOpen(false);
      }
    },
    [activeOrganizationId, activeProjectId, navigateToScope, queryClient]
  );

  const renderItemAction = useCallback(
    (entity: SwitcherEntity) => {
      const org = orgLookup.get(entity.id);
      // Each org row carries its own `can_edit` (backend-computed, mirroring the
      // org metadata update gate), so Edit shows on any org the caller may edit —
      // not just the active one — with no per-org capabilities call.
      if (!org?.can_edit) {
        return null;
      }
      return (
        <Button
          className="hidden size-7 shrink-0 group-hover:flex"
          onClick={(e) => {
            e.stopPropagation();
            setEditingOrganization(org);
            setIsEditDialogOpen(true);
            setIsOpen(false);
          }}
          size="icon"
          type="button"
          variant="ghost"
        >
          <Pencil className="size-3.5" />
        </Button>
      );
    },
    [orgLookup]
  );

  if (!activeOrganizationId) {
    return null;
  }

  return (
    <>
      <BaseOrganizationSwitcher
        activeOrganizationId={activeOrganizationId}
        onOpenChange={setIsOpen}
        onSelect={handleSelect}
        open={isOpen}
        organizations={entities}
        renderItemAction={renderItemAction}
        triggerClassName={triggerClassName}
        triggerSize={triggerSize}
        triggerVariant={triggerVariant}
      >
        {canManageOrg && (
          <>
            <DropdownMenuSeparator className="my-2" />
            <div className="px-2 pb-1 font-medium text-muted-foreground text-xs">
              Actions
            </div>
            <SwitcherItem
              from={ORG_PROJECT_ROUTE_PATH}
              to="./organization"
              value="action:manage-org"
            >
              <Settings className="size-4 text-muted-foreground" />
              <span className="font-medium text-sm">Manage organization</span>
            </SwitcherItem>
          </>
        )}
      </BaseOrganizationSwitcher>

      <Dialog onOpenChange={setIsEditDialogOpen} open={isEditDialogOpen}>
        {!!editingOrganization && (
          <EditOrganizationDialog
            organization={editingOrganization}
            setIsOpen={setIsEditDialogOpen}
          />
        )}
      </Dialog>
    </>
  );
}
